import { createFileRoute, Link } from '@tanstack/react-router';
import { useState } from 'react';
import {
  useReverseYearEndClose,
  useRunYearEndClose,
  useYearEndClosings,
  useYearEndPreview,
} from '../../hooks/useYearEnd';
import { useActiveCompanyId } from '../../stores/useCompanyStore';
import { formatAccountingDate, formatCurrency } from '../../lib/formatters';
import { ApiError } from '../../lib/api';

export const Route = createFileRoute('/_auth/year-end/')({
  component: YearEndPage,
});

function YearEndPage() {
  const companyId = useActiveCompanyId();
  const thisYear = new Date().getFullYear();
  const [fiscalYear, setFiscalYear] = useState(thisYear - 1);
  const [asOfDate, setAsOfDate] = useState(`${thisYear - 1}-12-31`);
  const { data: preview, isLoading } = useYearEndPreview(companyId, fiscalYear, asOfDate);
  const { data: closings = [] } = useYearEndClosings(companyId);
  const runClose = useRunYearEndClose(companyId);
  const reverse = useReverseYearEndClose(companyId);

  if (!companyId) {
    return (
      <div className="card" style={{ textAlign: 'center', padding: 32 }}>
        <h1 style={{ marginTop: 0 }}>Year-End Close</h1>
        <p className="muted">No active company. Year-end close is per-company.</p>
      </div>
    );
  }

  const runError =
    runClose.error instanceof ApiError
      ? (runClose.error.body as { error?: string } | null)?.error ?? 'Close failed'
      : runClose.error?.message;

  return (
    <div>
      <h1 style={{ fontSize: 24, margin: '0 0 4px' }}>Year-End Close</h1>
      <p className="muted" style={{ marginTop: 0, fontSize: 13 }}>
        Closes income & expense into Retained Earnings with one balanced entry. Income/expense start the next year at zero.
      </p>

      <div className="card" style={{ padding: 16, marginBottom: 16, maxWidth: 760 }}>
        <div style={{ display: 'flex', gap: 16, alignItems: 'center', flexWrap: 'wrap', fontSize: 13 }}>
          <label>Fiscal year{' '}
            <select value={fiscalYear} onChange={(e) => setFiscalYear(Number(e.target.value))}>
              {[thisYear - 2, thisYear - 1, thisYear].map((y) => <option key={y} value={y}>{y}</option>)}
            </select>
          </label>
          <label>Close date{' '}
            <input type="date" value={asOfDate} onChange={(e) => setAsOfDate(e.target.value)} />
          </label>
        </div>

        {isLoading && <p className="muted" style={{ marginBottom: 0 }}>Loading preview…</p>}

        {preview && (
          <div style={{ marginTop: 12 }}>
            {preview.blockers.map((b, i) => (
              <div key={i} className="field-error" style={{ marginBottom: 6 }}>⛔ {b}</div>
            ))}
            {preview.warnings.map((w, i) => (
              <div key={i} style={{ color: 'var(--accent)', marginBottom: 6, fontSize: 13 }}>⚠ {w}</div>
            ))}
            <div style={{ display: 'flex', gap: 24, alignItems: 'baseline', marginTop: 8, flexWrap: 'wrap' }}>
              <div>
                <span className="muted" style={{ fontSize: 12 }}>Net income to close → Retained Earnings ({preview.retainedEarningsCode})</span>
                <div style={{ fontSize: 22, fontWeight: 700 }}>{formatCurrency(preview.netIncome)}</div>
              </div>
              <button
                className="btn-primary"
                disabled={!preview.canClose || runClose.isPending}
                onClick={() => {
                  if (confirm(`Close FY ${fiscalYear} as of ${asOfDate}? This posts the closing entry.`)) {
                    runClose.mutate({ fiscalYear, asOfDate });
                  }
                }}
              >
                {runClose.isPending ? 'Closing…' : `Close FY ${fiscalYear}`}
              </button>
            </div>
            {runError && <div className="field-error" style={{ marginTop: 10 }}>{runError}</div>}
          </div>
        )}
      </div>

      {preview && preview.lines.length > 0 && (
        <div className="card" style={{ padding: 16, marginBottom: 16 }}>
          <h2 style={{ fontSize: 15, marginTop: 0 }}>Accounts to be closed</h2>
          <table className="table">
            <thead>
              <tr>
                <th>Account</th>
                <th>Type</th>
                <th style={{ textAlign: 'right' }}>Closing entry</th>
              </tr>
            </thead>
            <tbody>
              {preview.lines.map((l) => (
                <tr key={l.accountId}>
                  <td>{l.code} — {l.name}</td>
                  <td className="muted">{l.accountType.replace(/_/g, ' ')}</td>
                  <td style={{ textAlign: 'right' }}>{l.side === 'debit' ? 'DR' : 'CR'} {formatCurrency(l.amount)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {closings.length > 0 && (
        <div className="card" style={{ padding: 16 }}>
          <h2 style={{ fontSize: 15, marginTop: 0 }}>Close history</h2>
          <table className="table">
            <thead>
              <tr>
                <th>Fiscal year</th>
                <th>As of</th>
                <th style={{ textAlign: 'right' }}>Net income</th>
                <th>Status</th>
                <th>Entry</th>
                <th style={{ width: 1 }}></th>
              </tr>
            </thead>
            <tbody>
              {closings.map((c) => (
                <tr key={c.id} style={{ opacity: c.status === 'reversed' ? 0.6 : 1 }}>
                  <td>{c.fiscalYear}</td>
                  <td>{formatAccountingDate(c.asOfDate)}</td>
                  <td style={{ textAlign: 'right' }}>{formatCurrency(c.netIncome)}</td>
                  <td>{c.status === 'closed' ? <span className="tag ok">closed</span> : <span className="tag bad">reversed</span>}</td>
                  <td><Link to="/journal/$id" params={{ id: c.journalEntryId }}>view</Link></td>
                  <td>
                    {c.status === 'closed' && (
                      <button
                        className="btn-danger"
                        disabled={reverse.isPending}
                        onClick={() => {
                          const reason = prompt('Reason for reversing this year-end close?');
                          if (reason && reason.trim()) reverse.mutate({ id: c.id, reason: reason.trim() });
                        }}
                      >
                        Reverse
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
